import { Paper } from '@components'
import { accountContext } from '@contexts'
import { css } from '@emotion/react'
import { t } from '@lingui/core/macro'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import { api } from 'api'
import { boolean, func, number, oneOfType, string } from 'prop-types'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as SnackbarActions from '../../../global/snackbar/snackbar.actions'
import { isOauthType, isOauthWithFieldsType, SaveAccount } from './components'
import * as Platforms from './platforms/'

const padding = css`
  padding: 12px;
`

const paperStyle = css`
  height: 100px;
  overflow: hidden;
  padding: 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  box-shadow:
    0 3px 3px -2px #264653,
    0 3px 4px 0 #264653,
    0 1px 8px 0 #264653;
`

const accountComponents = {
  adobe: Platforms.Adobe,
  'adobe-experience-manager': Platforms.AdobeExperienceManager,
  aws: Platforms.AWS,
  sftp: Platforms.SFTP,
  'google-cloud': Platforms.GoogleCloud,
  'google-dcm': Platforms.GoogleDCM,
  'google-dv360': Platforms.GoogleDV360,
  workfront: Platforms.Workfront,
  'qr-code-monkey': Platforms.QrCodeMonkey,
  'azure-blob': Platforms.AzureBlob,
  'google-ads': Platforms.GoogleAds,
  branch: Platforms.Branch,
  snapchat: Platforms.Snapchat,
  tiktok: Platforms.Tiktok,
  snowflake: Platforms.Snowflake,
  'salesforce-marketing-cloud': Platforms.SalesforceMarketingCloud,
}

export const EditAccount = ({ accountId = null, onClose = () => {}, newConnection }) => {
  const { byId, updateById } = useContext(accountContext)
  const account = useMemo(() => byId[accountId], [byId, accountId])
  const [saving, setSaving] = useState(false)
  const [requiredFieldMissing, setRequiredFieldMissing] = useState(false)
  const [customFieldsInvalid, setCustomFieldsInvalid] = useState(false)
  const [accountTypes, setAccountTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const dispatch = useDispatch()

  const [updatedAccount, setUpdatedAccount] = useState(
    accountId
      ? { ...account, metadata: account.metadata ? account.metadata : {} }
      : {
          name: '',
          description: '',
          type: '',
          metadata: {},
        },
  )

  const showInactiveConnectors = useSelector(
    state => state.Auth.features['show-inactive-connectors'],
  )

  useEffect(() => {
    api.get('/api/v1/connector-type').then(({ data }) => {
      const sorted = data.data.sort((a, b) => (a.name || '').localeCompare(b.name))
      setAccountTypes(showInactiveConnectors ? sorted : sorted.filter(con => con.active))
      setLoading(false)
    })
  }, [])

  useEffect(() => {
    if (accountId) {
      api.get(`/api/v1/account/connector/${accountId}/metadata`).then(({ data }) => {
        setUpdatedAccount({ ...updatedAccount, metadata: data })
        setLoading(false)
      })
    }
  }, [accountId])

  const connectOAuthAccount = id => {
    const data = {
      connector_id: id,
      connector_name: updatedAccount.name,
      connector_description: updatedAccount.description,
      connector_type_id: updatedAccount.connector_type_id,
      connector_type_slug: updatedAccount.connector_type.slug,
    }
    api
      .post(`/api/v1/oauth/init/${data.connector_type_slug}`, data)
      .then(response => {
        window.location = response.data
      })
      .catch(() => {
        setSaving(false)
        dispatch(
          SnackbarActions.showSnackbar({
            message: t`Unable to redirect to connected account to gain access. Please try again.`,
            type: 'ERROR',
          }),
        )
      })
  }

  const saveConnector = withOauth => {
    const connectorSlug = updatedAccount.connector_type.slug

    const payload = {
      ...updatedAccount,
      connector_type_slug: connectorSlug,
    }
    return api
      .post(`/api/v1/account/connector/`, payload)
      .then(({ data }) => {
        updateById(data)
        if (withOauth) {
          connectOAuthAccount(data.id)
        } else {
          dispatch(
            SnackbarActions.showSnackbar({
              message: t`Connected Account saved successfully`,
              type: 'SUCCESS',
            }),
          )
          onClose()
        }
      })
      .catch(reason => {
        setSaving(false)
        dispatch(
          SnackbarActions.showSnackbar({
            message:
              reason?.data?.error?.message ??
              t('Connected Account failed to save. The given data was invalid.'),
            type: 'ERROR',
          }),
        )
      })
  }

  const handleSave = async () => {
    setSaving(true)
    const connectorSlug = updatedAccount.connector_type.slug

    if (isOauthType(connectorSlug)) {
      connectOAuthAccount(updatedAccount.id || null)
      return
    }

    saveConnector(isOauthWithFieldsType(connectorSlug))
  }

  const getHelperText = showText => (showText ? '*Required' : null)

  const errorProps = bool =>
    bool
      ? {
          helperText: '*Required',
          error: 'error',
          id: 'filled-error-helper-text',
        }
      : {}

  const Account =
    updatedAccount.connector_type &&
    !isOauthType(updatedAccount.connector_type.slug) &&
    accountComponents[updatedAccount.connector_type.slug]

  return !updatedAccount || loading ? (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '30vh',
      }}
      data-testid="edit-account-progress-wrapper"
    >
      <CircularProgress color="primary" data-testid="circular-progress" />
    </div>
  ) : (
    <div css={padding}>
      <div style={{ marginTop: 12, marginBottom: 12 }}>
        <TextField
          variant="outlined"
          label={t`Account name`}
          data-test-atr="account-input-container"
          inputProps={{
            'data-test-id': 'account-name-input',
          }}
          required
          fullWidth
          onChange={e => {
            setUpdatedAccount({ ...updatedAccount, name: e.target.value })
          }}
          value={updatedAccount.name ?? ''}
          {...errorProps(!updatedAccount.name)}
        />
      </div>
      <div>
        <TextField
          label={t`Description`}
          fullWidth
          variant="outlined"
          data-test-atr="description-input-container"
          inputProps={{
            'data-test-id': 'description-input',
          }}
          onChange={e => {
            setUpdatedAccount({ ...updatedAccount, description: e.target.value })
          }}
          value={updatedAccount.description ?? ''}
        />
      </div>

      {accountId ? (
        <Grid container item xs={12} spacing={1} style={{ marginTop: 12, marginBottom: 12 }}>
          <Grid item xs={3}>
            <Paper elevation={1} css={paperStyle} data-testid="edit-account-paper">
              <img
                style={{ maxWidth: 35, paddingBottom: 10 }}
                alt={updatedAccount.connector_type.slug}
                data-testid="edit-account-img"
                src={`https://claravine-public-prod.s3-us-west-2.amazonaws.com/images/${updatedAccount.connector_type.slug}.png`}
              />
              <div style={{ textAlign: 'center' }}>{updatedAccount.connector_type.name}</div>
            </Paper>
          </Grid>
        </Grid>
      ) : (
        <Grid container item xs={12} spacing={1} style={{ marginTop: 12, marginBottom: 12 }}>
          {accountTypes.map(type => (
            <Grid key={type.id} item xs={3}>
              <Paper
                elevation={1}
                style={{
                  height: 100,
                  overflow: 'hidden',
                  padding: 12,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  border: `3px solid ${type.id === updatedAccount.connector_type_id ? '#3E8FF7' : 'transparent'}`,
                }}
                data-testid="edit-account-paper"
                onClick={() => {
                  setUpdatedAccount({
                    ...updatedAccount,
                    connector_type_id: type.id,
                    connector_type: { ...type },
                  })
                }}
              >
                <img
                  style={{ maxWidth: 35, paddingBottom: 10 }}
                  alt={type.slug}
                  data-testid="edit-account-img"
                  src={`https://claravine-public-prod.s3-us-west-2.amazonaws.com/images/${type.slug}.png`}
                />
                <div style={{ textAlign: 'center', fontSize: 13, lineHeight: '14px' }}>
                  {type.name}
                </div>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
      {!loading &&
        updatedAccount.connector_type &&
        !isOauthType(updatedAccount.connector_type.slug) &&
        Account && (
          <Account
            disabled={!updatedAccount.name}
            connectOAuthAccount={connectOAuthAccount}
            accountId={accountId}
            updatedAccount={updatedAccount}
            setUpdatedAccount={setUpdatedAccount}
            setRequiredFieldMissing={setRequiredFieldMissing}
            setCustomFieldsInvalid={setCustomFieldsInvalid}
            newConnection={newConnection}
            getHelperText={getHelperText}
          />
        )}
      <SaveAccount
        data-test-atr="save-account-button"
        connectorSlug={updatedAccount?.connector_type?.slug || ''}
        isSaving={saving}
        submitDisabled={
          !updatedAccount.connector_type ||
          saving ||
          !updatedAccount.name ||
          requiredFieldMissing ||
          customFieldsInvalid
        }
        onSave={handleSave}
        isEdit={!newConnection}
      />
    </div>
  )
}

EditAccount.propTypes = {
  accountId: oneOfType([string, number]),
  onClose: func,
  newConnection: boolean,
}
